// Email-safe HTML sanitiser for rich-text inputs (body, signature).
// Whitelist-based — drops anything not on ALLOWED_TAGS / ALLOWED_ATTR.
// Strips javascript: URLs and any on* event handlers regardless. Runs in
// the browser via the DOMParser API; no dependencies. The output is what
// gets persisted to Firestore and what the template render emits inside
// the email iframe — both contexts demand the same rigour.

const ALLOWED_TAGS = new Set([
  'P', 'BR', 'DIV', 'SPAN',
  'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'MARK',
  'A', 'IMG',
  'UL', 'OL', 'LI',
  'BLOCKQUOTE', 'HR',
  'H1', 'H2', 'H3', 'H4',
]);

const ALLOWED_ATTR = {
  '*': ['style'],
  A: ['href', 'target', 'rel'],
  IMG: ['src', 'alt', 'width', 'height'],
};

function isSafeUrl(url) {
  if (!url) return false;
  const trimmed = String(url).trim().toLowerCase();
  return trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('mailto:') || trimmed.startsWith('tel:');
}

function sanitiseStyle(value) {
  if (!value) return '';
  // Drop expressions, url(javascript:…), behavior: properties.
  if (/expression\(|javascript:|behavi[oo]r:|@import/i.test(value)) return '';
  // Cap to a sensible length so a single inline style can't bloat email.
  return value.slice(0, 400);
}

export function sanitiseEmailHtml(html) {
  if (!html) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<!doctype html><body>${html}</body>`, 'text/html');
  walk(doc.body);
  return doc.body.innerHTML;
}

function walk(node) {
  const children = Array.from(node.childNodes);
  for (const child of children) {
    if (child.nodeType === 3) continue; // text node — fine
    if (child.nodeType !== 1) { child.remove(); continue; }
    const tag = child.tagName;
    if (!ALLOWED_TAGS.has(tag)) {
      // Replace disallowed element with its text content so we don't lose copy.
      const text = doc(child).createTextNode(child.textContent || '');
      child.replaceWith(text);
      continue;
    }
    // Strip unsafe attributes.
    const allowed = new Set([
      ...(ALLOWED_ATTR['*'] || []),
      ...(ALLOWED_ATTR[tag] || []),
    ]);
    for (const attr of Array.from(child.attributes)) {
      const name = attr.name;
      const lower = name.toLowerCase();
      if (lower.startsWith('on')) { child.removeAttribute(name); continue; }
      if (!allowed.has(lower)) { child.removeAttribute(name); continue; }
      // URL-bearing attrs
      if (tag === 'A' && lower === 'href' && !isSafeUrl(attr.value)) {
        child.removeAttribute(name); continue;
      }
      if (tag === 'IMG' && lower === 'src' && !isSafeUrl(attr.value)) {
        child.removeAttribute(name); continue;
      }
      if (lower === 'style') {
        const cleaned = sanitiseStyle(attr.value);
        if (cleaned) child.setAttribute('style', cleaned);
        else child.removeAttribute(name);
        continue;
      }
    }
    // Force rel + target on links so opens in a new tab and won't leak referrer.
    if (tag === 'A') {
      child.setAttribute('target', '_blank');
      child.setAttribute('rel', 'noopener noreferrer');
    }
    walk(child);
  }
}

// Tiny helper so walk() doesn't reach for window.document.
function doc(el) { return el.ownerDocument; }
